import React, { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useSiteSettings } from '../hooks/useSiteSettings';
import './About.css';

const AboutUsPage = () => {
  const { site_name } = useSiteSettings();
  const [about, setAbout] = useState({ 
    title: '', 
    content: '', 
    image: '', 
    vision: '', 
    mission: '', 
    strategic_goals: '', 
    values: '', 
    org_structure: '', 
    work_fields: '' 
  });
  const [loading, setLoading] = useState(true);

  const fetchAbout = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/basic-info/about`);
      const data = await res.json();
      setAbout({
        title: data.title || 'من نحن',
        content: data.content || '',
        image: data.image || '',
        vision: data.vision || '',
        mission: data.mission || '',
        strategic_goals: data.strategic_goals || '',
        values: data.values || '',
        org_structure: data.org_structure || '',
        work_fields: data.work_fields || ''
      });
    } catch (error) {
      console.error('خطأ في جلب بيانات من نحن:', error);
      setAbout({
        title: 'من نحن',
        content: '',
        image: '',
        vision: '',
        mission: '',
        strategic_goals: '',
        values: '',
        org_structure: '',
        work_fields: ''
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAbout();
  }, [fetchAbout]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>جاري التحميل...</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{about.title || 'من نحن'} | مؤسسة ثراء الخير للمقاولات والديكور</title>
        <meta name="description" content="تعرف على مؤسسة ثراء الخير، خيارك الأول في المقاولات والتشطيبات والديكورات الحديثة. نقدم خدمات دهانات، إيبوكسي، تركيب بديل الخشب، وأعمال الألومنيوم." />
      </Helmet>
      
      <div className="about-page">
        {/* Hero Section */}
        <div className="about-hero">
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="arabic-text">من نحن</span>
              <span className="english-text">About Us</span>
            </h1>
          </div>
          <div className="hero-image">
            {about.image && (
              <img src={about.image} alt="مؤسسة ثراء الخير للمقاولات" />
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="about-container">
          
          {/* About Us Section */}
          <section className="about-section">
            <div className="section-header">
              <h2 className="section-title">
                <span className="arabic-text">من نحن</span>
                <span className="english-text">Who We Are</span>
              </h2>
              <div className="title-underline"></div>
            </div>
            <div className="content-card">
              {/* تمت إضافة النصوص التسويقية مع وسوم HTML لجوجل */}
              <div 
                className="content-text" 
                dangerouslySetInnerHTML={{ 
                  __html: about.content || 
                  `
                    <p style="line-height: 1.8; font-size: 16px;">
                      تأسست <strong>مؤسسة ثراء الخير</strong> لتكون الرائدة في تقديم خدمات <strong>مقاولات وتشطيبات</strong> متكاملة تلبي تطلعات عملائنا. 
                      نحن نؤمن بأن كل مساحة لها روحها الخاصة، ولذلك نحرص على تقديم <strong>تصميم داخلي</strong> يعكس شخصيتك، 
                      وتنفيذ <strong>ديكورات حديثة</strong> تضفي طابعاً من الرقي والفخامة على مكانك.
                    </p>
                    <p style="line-height: 1.8; font-size: 16px; margin-top: 15px;">
                      على مدار سنوات عملنا، كسبنا ثقة عملائنا لنصبح <strong>أفضل شركة ديكورات ومقاول تشطيبات</strong> يعتمد عليه. 
                      فريقنا يتكون من نخبة من المهندسين والفنيين، بدءاً من <strong>معلم دهانات</strong> محترف لتنفيذ الدهانات الداخلية والخارجية، 
                      وصولاً إلى خبراء تركيب <strong>بديل الخشب وبديل الرخام</strong> وتجهيز <strong>واجهات الاستركشر والزجاج</strong> ودهانات الإيبوكسي للأرضيات.
                    </p>
                  ` 
                }} 
              />
            </div>
          </section>

          {/* Vision Section */}
          <section className="about-section">
            <div className="section-header">
              <h2 className="section-title">
                <span className="arabic-text">الرؤية</span>
                <span className="english-text">Vision</span>
              </h2>
              <div className="title-underline"></div>
            </div>
            <div className="content-card vision-card">
              <div className="card-icon">👁️</div>
              <div className="content-text">
                {about.vision || 'أن نكون الخيار الأول والشركة الرائدة في مجال المقاولات والتشطيبات والديكورات الحديثة، وأن نضع بصمة من الفخامة والابتكار في كل منزل ومشروع نقوم بتنفيذه.'}
              </div>
            </div>
          </section>

          {/* Mission Section */}
          <section className="about-section">
            <div className="section-header">
              <h2 className="section-title">
                <span className="arabic-text">الرسالة</span>
                <span className="english-text">Mission</span>
              </h2>
              <div className="title-underline"></div>
            </div>
            <div className="content-card mission-card">
              <div className="card-icon">🎯</div>
              <div className="content-text">
                {about.mission || 'تقديم خدمات تشطيب شقق وفلل، وتنفيذ دهانات داخلية وخارجية، وأعمال الألومنيوم والزجاج بأعلى معايير الجودة والاحترافية، مع الالتزام التام بالمواعيد وإرضاء عملائنا.'}
              </div>
            </div>
          </section>

          {/* Strategic Goals Section */}
          <section className="about-section">
            <div className="section-header">
              <h2 className="section-title">
                <span className="arabic-text">أهدافنا</span>
                <span className="english-text"> Our Objectives </span>
              </h2>
              <div className="title-underline"></div>
            </div>
            <div className="content-card goals-card">
              <div className="card-icon">🎯</div>
              <div 
                className="content-text" 
                dangerouslySetInnerHTML={{ 
                  __html: about.strategic_goals || 
                  `
                    <ul style="line-height: 1.8; list-style-type: none; padding: 0;">
                      <li>✔ توسيع نطاق خدماتنا في مجالات الديكورات الحديثة وبديل الخشب.</li>
                      <li>✔ تقديم حلول هندسية مبتكرة في الإيبوكسي وأرضيات السيراميك.</li>
                      <li>✔ الريادة كأفضل مقاول لتطوير وترميم المنازل والفلل.</li>
                    </ul>
                  `
                }} 
              />
            </div>
          </section>

          {/* Values Section */}
          <section className="about-section">
            <div className="section-header">
              <h2 className="section-title">
                <span className="arabic-text"> قيمنا</span>
                <span className="english-text"> Our Values</span>
              </h2>
              <div className="title-underline"></div>
            </div>
            <div className="content-card values-card">
              <div className="card-icon">💎</div>
              <div 
                className="content-text" 
                dangerouslySetInnerHTML={{ 
                  __html: about.values || 
                  `
                    <p style="line-height: 1.8; text-align: center;">
                      <strong>الجودة العالية:</strong> لا نتنازل عن أفضل المواد والخامات.<br/>
                      <strong>الالتزام بالمواعيد:</strong> دقة تامة في تسليم المشاريع.<br/>
                      <strong>الابتكار:</strong> مواكبة أحدث صيحات التصميم الداخلي.<br/>
                      <strong>الشفافية:</strong> مصداقية تامة مع العميل في كافة مراحل التشطيب.
                    </p>
                  `
                }} 
              />
            </div>
          </section>

        </div>
      </div>
    </>
  );
};

export default AboutUsPage;